'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { Heart, MoreVertical, Edit, Trash, Flag } from 'lucide-react'
import { useRouter } from 'next/navigation'
import {
  useDeletePostMutation,
  useGetPostLikesQuery,
  useLikePostMutation,
  useListingDetailQuery,
  Listing,
  User,
} from 'graphql/generated/graphql'
import { getUserId } from '@/lib/utils'
import { RootState } from 'features/store'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { isUserAvailable } from 'helpers/authUser'
import { useAuth } from '@/lib/authContext'
import { ProfileUtils } from 'utils/profile'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type AuthorListing = Pick<Listing, 'name' | 'photo' | 'listing_id'>

type ProfileFeedPost = {
  id: number
  user_id: number
  user: User
  content: string
  slug?: string
  title?: string | null
  status: string
  updated_at?: string
  created_at?: string
  likeCount?: number
  post_medias?: { url?: string | null }[] | null
}

function formatRelativeShort(dateString?: string | null): string {
  if (!dateString) return ''
  const diffInSeconds = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 1000
  )
  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function stripHtml(html?: string | null): string {
  if (!html) return ''
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function getPostCardImage(post: ProfileFeedPost): string | null {
  const mediaUrl = post.post_medias?.[0]?.url
  if (mediaUrl) return mediaUrl
  const match = post.content?.match(/<img[^>]+src=["']([^"']+)["']/i)
  return match?.[1] || null
}

function getPostCaption(post: ProfileFeedPost): string {
  const title = post.title?.trim()
  const body = stripHtml(post.content)
  if (title && body) return `${title}. ${body}`
  return title || body
}

function getAuthorImage(user?: User | null, listing?: AuthorListing | null): string {
  if (!user) return '/assets/avatar/user-default.svg'
  const userType = user.user_type?.toLowerCase()
  if (userType === 'producer') {
    return listing?.photo || user.default_listing?.photo || '/assets/icons/tractor.svg'
  }
  if (userType === 'market') {
    return listing?.photo || user.default_listing?.photo || '/assets/icons/Market.svg'
  }
  return user.profile_photo_resource || '/assets/avatar/user-default.svg'
}

function getAuthorDisplayName(user?: User | null, listing?: AuthorListing | null): string {
  if (!user) return 'Member'
  const userType = user.user_type?.toLowerCase()
  if (userType === 'producer' || userType === 'market') {
    return ProfileUtils.getName(
      user,
      (listing ?? user.default_listing) as Listing | undefined
    )
  }
  return user.full_name?.split(' ')[0] || user.username || 'Member'
}

export function ProfileFeedPostCard({
  post,
  profileListing,
}: {
  post: ProfileFeedPost
  profileListing?: AuthorListing | null
}) {
  const router = useRouter()
  const user = useSelector((state: RootState) => state.culineerAuth.user)
  const userAvailable = isUserAvailable(user)
  const { checkAuth } = useAuth()
  const [isLiked, setIsLiked] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [avatarSrc, setAvatarSrc] = useState('/assets/avatar/user-default.svg')
  const [imageUrlState, setImageUrlState] = useState<string | null>(null)
  const [imageFailed, setImageFailed] = useState(false)
  const [relativeTime, setRelativeTime] = useState('')

  const { data: likesData, refetch: refetchPostLikes } = useGetPostLikesQuery({
    skip: !post?.id,
    variables: { postId: post?.id ?? 0 },
  })

  const [likePost] = useLikePostMutation()
  const [deletePost] = useDeletePostMutation()

  const postUser = post?.user
  const isBusinessAccount =
    postUser?.user_type?.toLowerCase() === 'producer' ||
    postUser?.user_type?.toLowerCase() === 'market'
  const listingId =
    profileListing?.listing_id ??
    postUser?.default_listing_id ??
    postUser?.default_listing?.listing_id

  const { data: listingData } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: { listingId },
    skip: !isBusinessAccount || !listingId || !!profileListing,
  })

  const listingForDisplay: AuthorListing | null | undefined =
    profileListing ?? listingData?.listingDetail ?? postUser?.default_listing

  const currentUserId = getUserId()
  const isPostOwner = currentUserId === post?.user_id
  const imageUrl = imageFailed ? null : imageUrlState ?? getPostCardImage(post)
  const caption = getPostCaption(post)
  const timestamp = post?.updated_at || post?.created_at
  const authorName = getAuthorDisplayName(postUser, listingForDisplay)
  const isDraft = post?.status?.toLowerCase() === 'draft'

  useEffect(() => {
    if (!post) return
    setAvatarSrc(getAuthorImage(postUser, listingForDisplay))
    setImageUrlState(getPostCardImage(post))
    setImageFailed(false)
  }, [post, postUser, listingForDisplay])

  useEffect(() => {
    setRelativeTime(formatRelativeShort(timestamp))
  }, [timestamp])

  useEffect(() => {
    if (likesData) {
      const userLiked = likesData.getPostLikes?.some(
        (like) => like.user_id === currentUserId
      )
      setIsLiked(!!userLiked)
    }
  }, [likesData?.getPostLikes, currentUserId])

  if (!post?.id) return null

  const openPost = () => {
    if (!post.slug) return
    router.push(`/post/${post.slug}`)
  }

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!checkAuth()) return
    try {
      await likePost({ variables: { postId: post.id } })
      refetchPostLikes()
    } catch {
      setIsLiked(!isLiked)
      toast.error('Failed to like post')
    }
  }

  const handleDeletePost = async () => {
    try {
      await deletePost({ variables: { postID: post.id } })
      toast.success('Post deleted successfully')
      router.refresh()
    } catch {
      toast.error('Failed to delete post')
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  return (
    <>
      <article
        className="flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-[#E6E6E6] bg-white transition-shadow hover:shadow-sm"
        onClick={openPost}
      >
        <div className="flex items-start justify-between gap-2 p-3 pb-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full bg-gray-100">
              <Image
                src={avatarSrc}
                alt={authorName}
                fill
                sizes="32px"
                className="object-cover"
                onError={() => setAvatarSrc('/assets/avatar/user-default.svg')}
              />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[#202020]">
                {authorName}
              </p>
              <p className="text-xs text-[#6B6B6B]">
                {relativeTime || '\u00a0'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {isPostOwner && isDraft && (
              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] text-[#4A4A4A]">
                Draft
              </span>
            )}
            {userAvailable && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full p-1 hover:bg-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4 text-[#6B6B6B]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-40" align="end">
                  {isPostOwner ? (
                    <>
                      <DropdownMenuItem
                        className="flex cursor-pointer items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          router.push(`/posts/edit/${post.slug}`)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                        <span>Edit Post</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="flex cursor-pointer items-center gap-2 text-red-500"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowDeleteConfirm(true)
                        }}
                      >
                        <Trash className="h-4 w-4" />
                        <span>Delete Post</span>
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem
                      className="flex cursor-pointer items-center gap-2 text-red-500"
                      onClick={(e) => {
                        e.stopPropagation()
                        toast('User reported')
                      }}
                    >
                      <Flag className="h-4 w-4" />
                      <span>Report User</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {imageUrl && (
          <div className="relative aspect-[4/3] w-full bg-[#F7F7F7]">
            <Image
              src={imageUrl}
              alt={post.title || 'Post image'}
              fill
              sizes="(max-width: 640px) 100vw, 50vw"
              className="object-cover"
              onError={() => setImageFailed(true)}
            />
            {isDraft && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="rounded bg-black/60 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  Draft
                </span>
              </div>
            )}
          </div>
        )}

        {caption && (
          <div className="flex-1 px-3 py-3">
            <p className="line-clamp-4 text-sm leading-relaxed text-[#4A4A4A]">
              {caption}
            </p>
          </div>
        )}

        <div
          className="mt-auto flex justify-center border-t border-[#F0F0F0] py-3"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className={`rounded-full p-1.5 transition-colors ${
              isLiked ? 'text-[#85C349]' : 'text-[#85C349]/70 hover:text-[#85C349]'
            }`}
            onClick={handleLike}
            aria-label="Like post"
          >
            <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
          </button>
        </div>
      </article>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeletePost}
        variant="destructive"
      />
    </>
  )
}
